import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { peekReturnTo } from "@/lib/returnTo";

const errorText = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return "Please check the email address and password.";
  return "Something went wrong. Please try again.";
};

const inputCls = "w-full rounded-lg border border-[#D9A441]/25 bg-[#0B0C15]/60 px-3.5 py-2.5 text-sm text-[#F8F3E6] placeholder-[#A3A5B8]/60 focus:border-[#D9A441]/70 focus:outline-none";
const linkCls = "text-[#E5C365] underline underline-offset-4 hover:text-[#F8F3E6]";

const TITLES = {
  signin: ["Sign in", "Use Google or your email address."],
  signup: ["Create an account", "Your first 3 readings are free, no card required."],
  forgot: ["Reset your password", "We'll email you a link to choose a new password."],
  sent: ["Check your inbox", ""],
};

export const SignInDialog = () => {
  const { authDialogOpen, closeAuthDialog, loginWithGoogle, completeSignIn, trackEmailStart } = useAuth();
  const [mode, setMode] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const go = (m) => { setMode(m); setError(""); setPassword(""); };
  const onOpenChange = (open) => {
    if (!open) { closeAuthDialog(); go("signin"); setNotice(""); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const origin_url = window.location.origin;
      if (mode === "signin") {
        trackEmailStart();
        const res = await api.post("/auth/email/login", { email, password });
        await completeSignIn(res.data, "email");
        go("signin");
      } else if (mode === "signup") {
        trackEmailStart();
        const res = await api.post("/auth/email/register", { email, password, name, origin_url, return_to: peekReturnTo() });
        setNotice(res.data.message);
        go("sent");
      } else if (mode === "forgot") {
        const res = await api.post("/auth/email/forgot", { email, origin_url });
        setNotice(res.data.message);
        go("sent");
      }
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const [title, desc] = TITLES[mode];
  return (
    <Dialog open={authDialogOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md border-[#D9A441]/30 bg-[#131422] text-[#F8F3E6]" data-testid="signin-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium text-[#F8F3E6]">{title}</DialogTitle>
          {desc && <DialogDescription className="text-[#A3A5B8]">{desc}</DialogDescription>}
        </DialogHeader>

        {mode === "sent" ? (
          <div className="space-y-4" data-testid="signin-sent">
            <p className="text-sm leading-relaxed text-[#F8F3E6]">{notice}</p>
            <p className="text-xs text-[#A3A5B8]">The link works once. Can't find it? Check your spam folder.</p>
            <button type="button" onClick={() => go("signin")} className={`text-sm ${linkCls}`} data-testid="signin-back-btn">Back to sign in</button>
          </div>
        ) : (
          <>
            {mode !== "forgot" && (
              <>
                <button
                  type="button"
                  onClick={loginWithGoogle}
                  data-testid="signin-google-btn"
                  className="w-full rounded-full border border-[#D9A441]/50 px-5 py-2.5 text-sm font-medium text-[#E5C365] transition-colors hover:bg-[#D9A441]/10"
                >
                  Continue with Google
                </button>
                <div className="flex items-center gap-3 text-xs text-[#A3A5B8]"><span className="h-px flex-1 bg-[#D9A441]/20" />or with email<span className="h-px flex-1 bg-[#D9A441]/20" /></div>
              </>
            )}
            <form onSubmit={submit} className="space-y-3" data-testid={`signin-form-${mode}`}>
              {mode === "signup" && (
                <input className={inputCls} placeholder="Your name (optional)" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} autoComplete="name" data-testid="signin-name-input" />
              )}
              <input className={inputCls} type="email" required placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" data-testid="signin-email-input" />
              {mode !== "forgot" && (
                <input className={inputCls} type="password" required minLength={mode === "signup" ? 8 : undefined} placeholder={mode === "signup" ? "Password (at least 8 characters)" : "Password"} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete={mode === "signup" ? "new-password" : "current-password"} data-testid="signin-password-input" />
              )}
              {error && <p role="alert" className="text-sm text-[#E8896B]" data-testid="signin-error">{error}</p>}
              <button type="submit" disabled={busy} data-testid="signin-submit-btn" className="w-full rounded-full bg-[#D9A441] px-5 py-2.5 text-sm font-medium text-[#0B0C15] transition-colors hover:bg-[#E5C365] disabled:opacity-60">
                {busy ? "One moment…" : mode === "signin" ? "Sign in" : mode === "signup" ? "Create account" : "Send reset link"}
              </button>
            </form>
            <div className="flex flex-wrap justify-between gap-2 text-xs text-[#A3A5B8]">
              {mode === "signin" ? (
                <>
                  <button type="button" onClick={() => go("signup")} className={linkCls} data-testid="signin-to-signup">Create an account</button>
                  <button type="button" onClick={() => go("forgot")} className={linkCls} data-testid="signin-to-forgot">Forgot password?</button>
                </>
              ) : (
                <button type="button" onClick={() => go("signin")} className={linkCls} data-testid="signin-to-signin">I already have an account</button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
